import React, { createContext, useEffect, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, PCRE_UNAUTHORIZED_EVENT } from "../utils/api";

export const AuthContext = createContext();

export const AUTH_PROFILE_CACHE_KEY = "pcre_auth_profile_cache_v1";
const AUTH_PROFILE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const readCachedProfile = () => {
  try {
    const raw = localStorage.getItem(AUTH_PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.ts || !parsed.data) return null;
    if (Date.now() - parsed.ts > AUTH_PROFILE_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
};

export const writeAuthProfileCache = (data) => {
  try {
    localStorage.setItem(
      AUTH_PROFILE_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch (_) {
    // ignore (e.g., private mode / storage full)
  }
};

export const clearAuthProfileCache = () => {
  try {
    localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
  } catch (_) {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const queryClient = useQueryClient();

  // If any API call returns 401, clear local auth state so ProtectedRoute redirects to login.
  // This handles session expiry and prevents stale cached auth from "keeping you logged in".
  useEffect(() => {
    const handleUnauthorized = () => {
      setAuth((prev) => (prev ? null : prev));
      clearAuthProfileCache();
      try {
        queryClient.clear();
      } catch (e) {
        // ignore
      }
    };

    try {
      window.addEventListener(PCRE_UNAUTHORIZED_EVENT, handleUnauthorized);
    } catch (e) {
      // ignore (non-browser env)
    }

    return () => {
      try {
        window.removeEventListener(PCRE_UNAUTHORIZED_EVENT, handleUnauthorized);
      } catch (e) {
        // ignore
      }
    };
  }, [queryClient]);

  // Hydrate auth state from server session once on app start
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Fast path: if we have a fresh cache, use it immediately.
        const cached = readCachedProfile();
        if (mounted && cached && cached.id && cached.username) {
          setAuth({
            userId: cached.id,
            username: cached.username,
            email: cached.email,
            firstName: cached.first_name,
            lastName: cached.last_name,
            subscriptionPlanName: cached.subscription_plan_name,
            subscriptionPlanLabel: cached.subscription_plan_label,
            isPaidSubscription: !!cached.is_paid_subscription,
            mustCompleteOnboarding: !!cached.must_complete_onboarding,
          });
        }

        // suppressUnauthorizedEvent: initial hydration is a session probe, not a
        // user-initiated call.  A 401 here is expected when no session exists yet
        // (e.g. during the Google OAuth callback flow) and must NOT fire the global
        // "unauthorised" event that would clear auth state set by other code paths.
        const res = await apiFetch("/api/auth/profile/", {
          suppressUnauthorizedEvent: true,
        });
        if (mounted && res.ok) {
          const data = await res.json();
          writeAuthProfileCache(data);
          setAuth({
            userId: data.id,
            username: data.username,
            email: data.email,
            firstName: data.first_name,
            lastName: data.last_name,
            subscriptionPlanName: data.subscription_plan_name,
            subscriptionPlanLabel: data.subscription_plan_label,
            isPaidSubscription: !!data.is_paid_subscription,
            mustCompleteOnboarding: !!data.must_complete_onboarding,
          });
        }
      } catch (_) {
        // ignore; unauthenticated or network error → leave auth as null
      } finally {
        if (mounted) setIsHydrated(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/auth/logout/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Logout error:", err);
    } finally {
      // Always clear client auth state and caches
      setAuth(null);
      clearAuthProfileCache();
      try {
        queryClient.clear();
      } catch (e) {
        // ignore
      }
    }
  }, [queryClient]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    auth,
    setAuth,
    logout,
    isHydrated
  }), [auth, logout, isHydrated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
